import { zodResolver } from '@hookform/resolvers/zod'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import Box from '@repo/ui/box'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getEnrollmentPickupPersonInfo } from '../../../../api/enrollments/get-enrollment-pickup-person-info'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  PickupPersonEnum,
  pickupPersonOptionsMapping
} from '../../../../lib/types/enums/enrollment/enrollment-pickup'
import { EnrollmentPickupInput } from '../../../../lib/types/models/enrollment/enrollment-pickup'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../../elements/inputs/controlled-phone-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type EnrollmentPickupFormProps = BaseFormProps<EnrollmentPickupInput, true> & {
  enrollmentId: number
  disabled?: boolean
}

const formSchema: (
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<EnrollmentPickupInput> = (t) =>
  z.object({
    person: createEnumSchema({
      fieldName: "Sélectionner qui viendra chercher l'enfant",
      enumType: PickupPersonEnum
    }),
    firstName: createStringSchema('Prénom'),
    lastName: createStringSchema('Nom'),
    phoneNumber: PHONE_NUMBER_SCHEMA(t)
  })

export function EnrollmentPickupForm({
  disabled,
  defaultValues,
  enrollmentId,
  ...validationButtonProps
}: EnrollmentPickupFormProps) {
  const { t } = useTranslate()
  const form = useForm<EnrollmentPickupInput>({
    resolver: zodResolver(formSchema(t)),
    defaultValues,
    mode: FORM_MODE
  })

  const handleSelectionChange = async (person: PickupPersonEnum) => {
    form.setValue('person', person)

    if (person === PickupPersonEnum.OTHER) {
      form.setValue('firstName', null)
      form.setValue('lastName', null)
      form.setValue('phoneNumber', null)
    } else {
      const personInfo = await getEnrollmentPickupPersonInfo(enrollmentId, person)
      if (personInfo?.firstName) form.setValue('firstName', personInfo.firstName)
      else form.setValue('firstName', null)
      if (personInfo?.lastName) form.setValue('lastName', personInfo.lastName)
      else form.setValue('lastName', null)
      if (personInfo?.phoneNumber) form.setValue('phoneNumber', personInfo.phoneNumber)
      else form.setValue('phoneNumber', null)
    }
    form.trigger()
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <Box className="grid mobile:grid-cols-1 grid-cols-3 gap-4 border border-grey-light rounded-lg p-4">
          <FormField
            control={form.control}
            name={'person'}
            render={({ field }) => (
              <FormItem className={'col-span-3 mobile:col-span-1'}>
                <FormControl>
                  <ControlledSelectInput
                    {...field}
                    required
                    label={"Sélectionner qui viendra chercher l'enfant"}
                    disabled={disabled}
                    options={Object.values(PickupPersonEnum).map((value) => ({
                      label: pickupPersonOptionsMapping[value],
                      value
                    }))}
                    selectedOption={conditionalRender(!!field?.value, {
                      label: pickupPersonOptionsMapping[field.value as PickupPersonEnum],
                      value: field.value
                    })}
                    onChange={(value) => {
                      field.onChange(value)
                      if (value) handleSelectionChange(value)
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'lastName'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type={'text'}
                    label={'Nom'}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'firstName'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledInput
                    {...field}
                    required
                    type={'text'}
                    label={'Prénom'}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name={'phoneNumber'}
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <ControlledPhoneInput
                    {...field}
                    required
                    label={'Numéro de téléphone'}
                    defaultCountry={'FR'}
                    disabled={disabled}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </Box>

        <ValidationButtons {...validationButtonProps} />
      </form>
    </Form>
  )
}
